import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShieldCheck, FileText, Camera, Upload, Eye, Trash2, Loader, CheckCircle,
  AlertTriangle, Clock, Save, X, Pencil,
} from 'lucide-react';
import { uprightJpegBytes } from '../utils/imageOrient';
import { DMS_TYPES, DMS_GROUPS, DMS_STATUS, SOURCE_LABEL } from '../config/dmsTypes';
import VisaDetailsPanel from './VisaDetailsPanel';

// "My Documents" — the driver's own compliance-document shelf (the iComply
// replacement). Drivers upload/manage their OWN identity & vehicle documents;
// OCR pre-fills the number + expiry so they barely type. Structured items
// (company / emergency / address / scanner asset #) are simple forms. Only this
// driver + admins can ever see any of it. Talks to driver-dms-sb.js.

const API = '/.netlify/functions/driver-dms-sb';
// Cap the ENCODED payload: base64 inflates bytes by ~33%, and Netlify's function
// body limit (6 MB) applies to the JSON we post, not the raw file. Checking the
// raw size would let a 5 MB photo through as a 6.9 MB body that never arrives.
const MAX_B64 = 5_000_000;
// The only image types the OCR accepts. An iPhone HEIC is NOT one of them.
const SUPPORTED = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}
function daysUntil(d) {
  if (!d) return null;
  const t = new Date(d + 'T00:00:00').getTime();
  if (!t) return null;
  return Math.round((t - Date.now()) / 86400000);
}
function abToB64(buf) {
  let bin = ''; const bytes = new Uint8Array(buf); const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}
function b64ToBlob(base64, type) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return new Blob([bytes], { type: type || 'application/pdf' });
}
// <img>+canvas fallback for files createImageBitmap can't decode. Safari decodes
// HEIC natively here, which rescues iPhone library photos; Chrome can't, and we
// fail loudly below rather than shipping bytes the OCR is certain to reject.
function imgToJpegArrayBuffer(file, maxEdge = 2000, quality = 0.85) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => {
      try {
        const scale = Math.min(1, maxEdge / Math.max(im.naturalWidth, im.naturalHeight));
        const w = Math.max(1, Math.round(im.naturalWidth * scale));
        const h = Math.max(1, Math.round(im.naturalHeight * scale));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d').drawImage(im, 0, 0, w, h);
        c.toBlob((b) => {
          URL.revokeObjectURL(url);
          if (b) b.arrayBuffer().then(resolve).catch(() => resolve(null)); else resolve(null);
        }, 'image/jpeg', quality);
      } catch { URL.revokeObjectURL(url); resolve(null); }
    };
    im.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    im.src = url;
  });
}

function packPage(buf, contentType, filename) {
  const base64 = abToB64(buf);
  if (base64.length > MAX_B64) {
    throw new Error('That file is too big to send. Please retake the photo, or use a smaller PDF.');
  }
  return { base64, contentType, filename };
}

// Normalise any picked file into something the OCR can actually read.
async function fileToPage(file) {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')) {
    return packPage(await file.arrayBuffer(), 'application/pdf', file.name || 'document.pdf');
  }
  // Always try to produce an upright BASELINE jpeg (fixes EXIF rotation too).
  let buf = await uprightJpegBytes(file, 2000, 0.85);
  let contentType = 'image/jpeg';
  if (!buf) buf = await imgToJpegArrayBuffer(file);
  if (!buf) {
    if (SUPPORTED.has(file.type)) { buf = await file.arrayBuffer(); contentType = file.type; }
    else {
      throw new Error(
        `This photo is in a format we can't read (${file.type || 'unknown'}). `
        + `Use the "Take photo" button, or set iPhone Settings › Camera › Formats to "Most Compatible" and try again.`
      );
    }
  }
  return packPage(buf, contentType, (file.name || 'photo.jpg').replace(/\.[^.]+$/, '') + '.jpg');
}

// Red dot counts only REQUIRED docs needing action — non-required items are hidden from
// the person, so they must not light the badge either. Exported so a portal shell can
// light the badge WITHOUT mounting this tab: the sorter portal no longer lands on
// Documents, and a count that only loads once you open the tab is a count you never see.
// A visa holder who hasn't consented to a VEVO check counts as one more thing to do —
// the office can't run the check until they have.
export function needsVevoConsent(docs) {
  const v = (docs || []).find(x => x.type === 'visa');
  return !!(v && v.id && !(v.fields && v.fields.vevoConsent && v.fields.vevoConsent.given));
}
export function countDmsAttention(docs) {
  return (docs || []).filter(x => x.required && (x.renewalRequested || x.status === 'expired' || x.status === 'expiring')).length
    + (needsVevoConsent(docs) ? 1 : 0);
}

export async function fetchDmsAttention() {
  const res = await fetch(API, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'getMyDms' }),
  });
  const d = await res.json();
  if (!d || !d.success) return 0;
  return countDmsAttention(d.documents || []);
}

export default function MyDmsPortalTab({ shift, onCountChange, embedded, onVaultChanged }) {
  const [items, setItems] = useState(null);   // null = loading; map by type
  const [linkedDocs, setLinkedDocs] = useState([]);   // primary holder's docs surfaced to a dependent
  const [primaryLink, setPrimaryLink] = useState(null); // { login, name } when linked to a primary holder
  const [error, setError] = useState('');
  const [editing, setEditing] = useState('');  // type key whose editor is open
  const [busy, setBusy] = useState('');

  const api = useCallback(async (payload) => {
    const res = await fetch(API, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  }, []);

  const load = useCallback(async () => {
    try {
      const d = await api({ action: 'getMyDms' });
      if (d && d.success) {
        const docs = d.documents || [];
        const by = {}; docs.forEach(x => { by[x.type] = x; });
        setItems(by);
        setLinkedDocs(d.linkedDocuments || []);
        setPrimaryLink(d.primaryLink || null);
        onCountChange && onCountChange(countDmsAttention(docs));
      } else { setItems({}); setError(d?.error || 'Could not load your documents'); }
    } catch (e) { setItems({}); setError(e.message); }
  }, [api, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const onView = async (item) => {
    const w = window.open('', '_blank');
    setBusy(item.type + ':view'); setError('');
    try {
      const d = await api({ action: 'getDocFile', id: item.id });
      if (!d || !d.success) throw new Error(d?.error || 'Could not open document');
      const url = URL.createObjectURL(b64ToBlob(d.base64, d.contentType));
      if (w) w.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); setError(e.message); } finally { setBusy(''); }
  };

  const onDelete = async (item) => {
    if (!window.confirm('Remove this document? A copy is retained on file for compliance, but it will no longer show as current.')) return;
    setBusy(item.type + ':del'); setError('');
    try {
      const d = await api({ action: 'deleteDoc', id: item.id });
      if (!d || !d.success) throw new Error(d?.error || 'Could not remove');
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  if (items === null) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={16} /> Loading your documents…</div>;
  }

  // Only the driver's REQUIRED documents belong in the portal — showing items the
  // office doesn't need would just confuse them into an unfriendly experience.
  const groupsToShow = DMS_GROUPS
    .map(group => ({ group, types: DMS_TYPES.filter(t => t.group === group && items[t.key]?.required) }))
    .filter(g => g.types.length);

  // Nothing required for this driver → render nothing (no dangling heading).
  if (embedded && !groupsToShow.length && !(items.visa && items.visa.id)) return null;

  const attention = DMS_TYPES.filter(t => {
    const it = items[t.key];
    return it && it.required && (it.renewalRequested || it.status === 'expired' || it.status === 'expiring');
  });

  return (
    <div style={{ width: '100%' }}>
      {!embedded ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <ShieldCheck size={18} color="var(--hp-gold)" />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>My Documents</span>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 16, lineHeight: 1.5 }}>
            Keep your official documents current. Snap a photo — we read the number and expiry for you. Only you and the office can see these.
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>
            Your documents on file
          </span>
          <span style={{ flex: 1, height: 1, background: 'var(--hp-border)' }} />
        </div>
      )}

      {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.8rem', marginBottom: 12 }}>{error}</div>}

      {attention.length > 0 && (
        <div style={{ border: '1px solid var(--hp-border)', borderLeft: '3px solid #b8860b', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 18, background: 'rgba(201,168,76,0.10)' }}>
          <div style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--hp-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} color="#b8860b" /> {attention.length} {attention.length === 1 ? 'document needs' : 'documents need'} attention
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>
            {attention.map(t => t.label).join(' · ')}
          </div>
        </div>
      )}

      {/* Consent sits at the top, not under the visa item: only REQUIRED documents are
          listed below, and for most visa holders the grant notice isn't one of them. */}
      {items.visa && items.visa.id && (
        <VevoConsentPanel consent={items.visa.fields && items.visa.fields.vevoConsent} api={api} reload={load} setError={setError} />
      )}

      {primaryLink && !primaryLink.verified && (
        <div style={{ border: '1px solid var(--hp-border)', borderLeft: '3px solid #b8860b', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 18, background: 'rgba(201,168,76,0.10)' }}>
          <div style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--hp-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={14} color="#b8860b" /> Partner link pending confirmation
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>
            You’ve linked <strong>{primaryLink.name}</strong> as your primary visa holder. Once the office confirms it, their Confirmation of Enrolment and Letter of Enrolment will cover yours automatically.
          </div>
        </div>
      )}

      {groupsToShow.map(({ group, types: groupTypes }) => {
        return (
          <div key={group} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>{group}</span>
              <span style={{ flex: 1, height: 1, background: 'var(--hp-border)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {groupTypes.map(t => (
                <DmsItem
                  key={t.key} type={t} item={items[t.key]}
                  open={editing === t.key}
                  onOpen={() => { setEditing(editing === t.key ? '' : t.key); setError(''); }}
                  onView={onView} onDelete={onDelete}
                  api={api} reload={load} busy={busy} setBusy={setBusy} setError={setError}
                  onDone={() => setEditing('')} onVaultChanged={onVaultChanged}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Linked from the primary holder — reference copies surfaced from a partner whose
          records are linked to this person's. Informational: they don't replace the
          dependent's own required documents (e.g. their own Visa Grant Notice). */}
      {linkedDocs.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>
              Linked from {primaryLink ? primaryLink.name : 'your partner'}
            </span>
            <span style={{ flex: 1, height: 1, background: 'var(--hp-border)' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {linkedDocs.map((ld, i) => (
              <div key={ld.type + i} style={{ border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, background: 'var(--hp-surface)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <FileText size={18} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>{ld.label}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, color: 'var(--hp-green)', background: 'rgba(46,125,50,0.10)' }}>
                    <CheckCircle size={11} /> On file with {ld.providedBy?.name || 'primary holder'}
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                  {ld.docNumber && <span>No. {ld.docNumber}</span>}
                  {ld.docNumber && ld.expiryDate && <span> · </span>}
                  {ld.expiryDate && <span>Expires {fmtDate(ld.expiryDate)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, optional }) {
  const s = DMS_STATUS[status] || DMS_STATUS.missing;
  const label = (status === 'missing' && optional) ? 'Optional' : s.label;
  const Icon = status === 'ok' ? CheckCircle : status === 'expired' ? AlertTriangle : status === 'missing' ? null : Clock;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.02em', color: s.color, background: s.bg, border: `1px solid ${s.bg === 'transparent' ? 'var(--hp-border)' : 'transparent'}` }}>
      {Icon && <Icon size={11} />} {label}
    </span>
  );
}

function DmsItem({ type, item, open, onOpen, onView, onDelete, api, reload, busy, setBusy, setError, onDone, onVaultChanged }) {
  // Satisfied via a linked partner (a dependent's CoE/LoE covered by the primary holder).
  const linked = item && item.providedByLink;
  // Satisfied by completing it in the portal instead of uploading the form (the TFN
  // declaration and the super choice). There's no file, so the item must not read
  // "REQUIRED — upload" at the same time as its pill says it's done.
  const portal = !!(item && item.satisfiedBy === 'portal');
  const status = item ? item.status : 'missing';
  const isFile = type.kind === 'file';
  const has = !!item && (linked || portal ? true : (isFile ? item.hasFile : status === 'ok'));
  const dUntil = daysUntil(item?.expiryDate);

  return (
    <div style={{
      border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14,
      background: status === 'expired' ? 'rgba(214,69,69,0.05)' : status === 'renewal' || status === 'expiring' ? 'rgba(201,168,76,0.06)' : 'var(--hp-surface)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {isFile ? <FileText size={18} color="var(--hp-gold)" style={{ flexShrink: 0, marginTop: 2 }} />
                : <Pencil size={18} color="var(--hp-gold)" style={{ flexShrink: 0, marginTop: 2 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>{type.label}</span>
            <StatusPill status={status} optional={type.optional} />
            {item?.required && !has && (
              <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.64rem', fontWeight: 800, letterSpacing: '0.03em', color: 'var(--hp-gold)', background: 'rgba(201,168,76,0.14)', border: '1px solid transparent' }}>REQUIRED</span>
            )}
          </div>

          {/* Satisfied by the linked primary holder (their CoE / LoE covers this). */}
          {linked && (
            <div style={{ fontSize: '0.74rem', color: 'var(--hp-green)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700 }}>
              <CheckCircle size={12} /> Provided by {item.providedByLink.name} (linked){item.expiryDate ? ` · expires ${fmtDate(item.expiryDate)}` : ''}
            </div>
          )}

          {/* Completed in the portal — no file to show, so say what we hold instead. */}
          {portal && (
            <div style={{ fontSize: '0.74rem', color: 'var(--hp-green)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700 }}>
              <CheckCircle size={12} /> {item.note || 'Completed in the portal'}
              {item.updatedAt ? ` · ${fmtDate(String(item.updatedAt).slice(0, 10))}` : ''}
            </div>
          )}
          {portal && (
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
              You can still upload the ATO form if you have one — we'll keep both.
            </div>
          )}

          {/* Current detail line */}
          {has && isFile && !linked && !portal && (
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
              {item.docNumber && <span>No. {item.docNumber}</span>}
              {item.docNumber && type.expiry && item.expiryDate && <span> · </span>}
              {type.expiry && item.expiryDate && (
                <span style={{ color: status === 'expired' ? 'var(--hp-red)' : status === 'expiring' ? '#b8860b' : 'var(--hp-dim)' }}>
                  {status === 'expired' ? 'Expired ' : 'Expires '}{fmtDate(item.expiryDate)}
                  {dUntil != null && status !== 'ok' && <> ({dUntil < 0 ? `${-dUntil}d ago` : `in ${dUntil}d`})</>}
                </span>
              )}
              {type.expiry && !item.expiryDate && <span>No expiry recorded</span>}
              {(type.extraFields || []).filter(f => !f.adminOnly).map(f => item.fields?.[f.key] ? (
                <span key={f.key}> · {f.label} {item.fields[f.key]}</span>
              ) : null)}
            </div>
          )}
          {has && isFile && item.fields?.sourceType === 'digital_screenshot' && (
            <div style={{ fontSize: '0.7rem', color: '#b8860b', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlertTriangle size={11} /> {SOURCE_LABEL.digital_screenshot.label}
            </div>
          )}
          {has && !isFile && (
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
              {(type.fields || []).filter(f => item.fields?.[f.key]).slice(0, 2).map(f => item.fields[f.key]).join(' · ') || 'On file'}
            </div>
          )}
          {!has && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>{type.help}</div>}

          {/* Citizenship rear page: the ACC number is on the BACK of the certificate.
              When the office flags it, ask specifically for the back (and suppress the
              generic renewal line below so the message isn't doubled up). */}
          {type.key === 'citizenship' && item?.fields?.rearPageNeeded && (
            <div style={{ fontSize: '0.74rem', color: '#b8860b', marginTop: 6, fontWeight: 700, display: 'flex', alignItems: 'flex-start', gap: 5, lineHeight: 1.5 }}>
              <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} /> Please add a photo of the <b>BACK</b> of your citizenship certificate — it has the ACC number we need. Tap {has ? 'Replace' : 'Upload'} and include the rear page.
            </div>
          )}
          {item?.renewalRequested && !(type.key === 'citizenship' && item?.fields?.rearPageNeeded) && (
            <div style={{ fontSize: '0.74rem', color: '#b8860b', marginTop: 6, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
              <AlertTriangle size={12} /> The office has asked you to supply an updated copy.
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        {has && isFile && !portal && (
          <ActBtn onClick={() => onView(item)} busy={busy === type.key + ':view'} icon={<Eye size={13} />} label="View" />
        )}
        <ActBtn onClick={onOpen} icon={isFile ? <Upload size={13} /> : <Pencil size={13} />}
          label={portal ? 'Upload the form' : has ? (isFile ? 'Replace' : 'Edit') : (isFile ? 'Upload' : 'Add details')} primary={!has} />
        {has && !portal && (
          <ActBtn onClick={() => onDelete(item)} busy={busy === type.key + ':del'} icon={<Trash2 size={13} />} label="Remove" />
        )}
      </div>

      {open && (isFile
        ? <FileEditor type={type} api={api} reload={reload} setBusy={setBusy} setError={setError} onDone={onDone} onVaultChanged={onVaultChanged} />
        : <FieldsEditor type={type} item={item} api={api} reload={reload} setBusy={setBusy} setError={setError} onDone={onDone} />
      )}

      {/* Visa grant carries structured immigration detail (type, work restrictions,
          study/partner add-ons) beyond the uploaded notice — edited independently. */}
      {type.key === 'visa' && (
        <VisaDetailsPanel
          initial={item?.fields?.visaDetails}
          studentHint={!!(item?.fields?.vevo && (item.fields.vevo.subclass === '500' || (item.fields.vevo.conditions || []).map(String).includes('8105')))}
          onSave={async (details) => {
            const d = await api({ action: 'saveVisaDetails', type: 'visa', visaDetails: details });
            if (d && d.success) await reload();
            else setError(d?.error || 'Could not save visa details');
            return d;
          }}
        />
      )}
    </div>
  );
}

// ── File upload editor: capture each side → OCR pre-fill → confirm → save ──────
// Two-sided cards (type.sides, e.g. the licence Front/Back) are captured one side
// at a time; the server combines them into a single 2-page PDF and OCR reads both.
function FileEditor({ type, api, reload, setBusy, setError, onDone, onVaultChanged }) {
  // Some documents come in more than one form (plastic licence card vs the
  // Service NSW digital licence). They carry the same fields in different places,
  // so ask once and then guide each capture. The chosen variant only drives the
  // capture prompts — OCR independently judges physical vs screenshot, so a
  // driver can't mislabel a screenshot into looking like a real card.
  const [variant, setVariant] = useState(type.variants ? null : { sides: type.sides, help: type.help });
  const sides = variant ? (variant.sides || null) : null;
  const need = sides ? sides.length : 1;
  const [pages, setPages] = useState([]);      // captured, in side order
  const [stage, setStage] = useState('pick');  // pick | reading | confirm | saving
  const [docNumber, setDocNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [extra, setExtra] = useState({});     // extraFields values (e.g. cardNumber)
  const [tfn, setTfn] = useState('');          // captureTfn types only — routed to the vault, never stored on the doc
  const [sourceType, setSourceType] = useState('');
  const [ocrNote, setOcrNote] = useState('');
  const [ocrBad, setOcrBad] = useState(false);
  const [ocrStatus, setOcrStatus] = useState('manual');
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const nextLabel = sides ? sides[pages.length] : null;

  const runOcr = async (pgs) => {
    setStage('reading'); setOcrNote(''); setOcrBad(false);
    let status = 'manual';
    try {
      const d = await api({
        action: 'ocrDoc', type: type.key,
        pages: pgs.map(p => ({ fileBase64: p.base64, contentType: p.contentType })),
        sideLabels: sides || [],
      });
      if (d && d.success) {
        setDocNumber(d.docNumber || '');
        setExpiryDate(d.expiryDate || '');
        setExtra(d.fields || {});
        if (type.captureTfn) setTfn(d.tfn || '');
        setSourceType(d.sourceType || '');
        status = 'ok';
        const gotTfn = type.captureTfn && d.tfn;
        setOcrNote((d.docNumber || d.expiryDate || gotTfn || Object.values(d.fields || {}).some(Boolean))
          ? 'We read these for you — please check they’re right.'
          : 'We couldn’t find the details on this one — please enter them.');
      } else {
        // Show the REAL reason (e.g. an unsupported photo format) — the generic
        // "couldn't read it" message is what hid the original failure.
        setOcrBad(true);
        setOcrNote(d?.error || 'We couldn’t read it automatically — please enter the details.');
      }
    } catch (e) { setOcrBad(true); setOcrNote(e.message); }
    setOcrStatus(status);
    setStage('confirm');
  };

  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    setError('');
    try {
      const p = await fileToPage(f);
      const next = [...pages, p];
      setPages(next);
      if (next.length >= need) await runOcr(next);
    } catch (err) { setError(err.message); }
  };

  const retake = () => { setPages([]); setStage('pick'); setOcrNote(''); setOcrBad(false); if (type.variants) setVariant(null); };

  const save = async () => {
    if (!pages.length) return;
    setStage('saving'); setBusy(type.key + ':save'); setError('');
    try {
      const cleanTfn = (tfn || '').replace(/\s+/g, '');
      const d = await api({
        action: 'uploadDoc', type: type.key,
        pages: pages.map(p => ({ fileBase64: p.base64, contentType: p.contentType })),
        sideLabels: sides || [],
        filename: pages.length > 1 ? `${type.key}.pdf` : pages[0].filename,
        docNumber, expiryDate: type.expiry ? expiryDate : '', ocrStatus,
        fields: extra, sourceType,
        // captureTfn only — the number is written to the encrypted vault server-side,
        // never onto the document. Validated 8–9 digits below before it's sent.
        ...(type.captureTfn && /^\d{8,9}$/.test(cleanTfn) ? { tfn: cleanTfn } : {}),
      });
      if (!d || !d.success) throw new Error(d?.error || 'Upload failed');
      // The form saved; the TFN store is separate and may have its own message.
      if (d.tfn && d.tfn.requested && !d.tfn.stored && d.tfn.error) { setError(d.tfn.error); }
      onVaultChanged && onVaultChanged();
      await reload(); onDone();
    } catch (e) { setError(e.message); setStage('confirm'); } finally { setBusy(''); }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--hp-border)' }}>
      {pages.length > 0 && <Thumbs pages={pages} sides={sides} />}

      {/* Which form of the document do they have? */}
      {stage === 'pick' && type.variants && !variant && (
        <>
          <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
            Which one do you have?
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {type.variants.map(v => (
              <ActBtn key={v.key} onClick={() => setVariant(v)} icon={<Camera size={14} />} label={v.label} primary />
            ))}
            <ActBtn onClick={onDone} icon={<X size={13} />} label="Cancel" />
          </div>
        </>
      )}

      {stage === 'pick' && variant && (
        <>
          <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginBottom: 10, lineHeight: 1.5 }}>
            {pages.length === 0 && variant.help && <div style={{ marginBottom: 6 }}>{variant.help}</div>}
            {sides
              ? <>Now take the <b style={{ color: 'var(--hp-text)' }}>{nextLabel}</b> ({pages.length + 1} of {need}).</>
              : type.help}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={pick} style={{ display: 'none' }} />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" onChange={pick} style={{ display: 'none' }} />
            <ActBtn onClick={() => camRef.current?.click()} icon={<Camera size={14} />}
              label={sides ? `Take ${(nextLabel || '').toLowerCase()} photo` : 'Take photo'} primary />
            <ActBtn onClick={() => fileRef.current?.click()} icon={<Upload size={14} />} label="Choose file" />
            <ActBtn onClick={pages.length ? retake : onDone} icon={<X size={13} />} label={pages.length ? 'Start over' : 'Cancel'} />
          </div>
          {/* Escape hatch if the second capture genuinely isn't available. The card
              number lives there, so warn rather than make it look optional. */}
          {pages.length > 0 && pages.length < need && (
            <button onClick={() => runOcr(pages)}
              style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: 'var(--hp-dim)', fontSize: '0.75rem', textDecoration: 'underline' }}>
              Continue without the {(nextLabel || '').toLowerCase()} — I’ll enter the card number myself
            </button>
          )}
        </>
      )}

      {stage === 'reading' && (
        <div style={{ padding: '12px 0', color: 'var(--hp-dim)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader size={14} /> Reading your document…
        </div>
      )}

      {(stage === 'confirm' || stage === 'saving') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ocrNote && (
            <div style={{ fontSize: '0.74rem', color: ocrBad ? 'var(--hp-red)' : 'var(--hp-dim)', lineHeight: 1.5 }}>
              {ocrNote}
            </div>
          )}
          {/* A screenshot of a digital licence isn't proof on its own — say so here
              rather than let it quietly pass as verified evidence. */}
          {sourceType === 'digital_screenshot' && (
            <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start', padding: '9px 11px', borderRadius: 'var(--radius)',
              background: 'rgba(201,168,76,0.10)', border: '1px solid var(--hp-border)', borderLeft: '3px solid #b8860b' }}>
              <AlertTriangle size={13} color="#b8860b" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
                {SOURCE_LABEL.digital_screenshot.note}
              </span>
            </div>
          )}
          {/* noNumber forms (TFN declaration, super choice) carry no document
              reference — only the person's own numbers. Don't ask for one. */}
          {!type.noNumber && (
            <Field label={type.numberLabel || 'Document number'}>
              <input value={docNumber} onChange={e => setDocNumber(e.target.value)} placeholder="—" style={inputStyle} />
            </Field>
          )}
          {(type.extraFields || []).filter(f => !f.adminOnly).map(f => (
            <Field key={f.key} label={f.label}>
              <input value={extra[f.key] || ''} placeholder={f.placeholder || '—'}
                onChange={e => setExtra(x => ({ ...x, [f.key]: e.target.value }))} style={inputStyle} />
            </Field>
          ))}
          {/* captureTfn: read off the form, confirmed here, then stored ENCRYPTED —
              it is never saved onto this document. inputMode numeric; 8–9 digits. */}
          {type.captureTfn && (
            <Field label="Tax File Number (stored securely)">
              <input value={tfn} onChange={e => setTfn(e.target.value.replace(/[^\d]/g, '').slice(0, 9))}
                inputMode="numeric" autoComplete="off" placeholder="8–9 digits" style={inputStyle} />
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginTop: 4, lineHeight: 1.4 }}>
                We read this off your form — please check it matches. It’s encrypted and never shown on the document.
              </div>
            </Field>
          )}
          {type.expiry && (
            <Field label="Expiry date">
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} style={inputStyle} />
            </Field>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <ActBtn onClick={save} busy={stage === 'saving'} icon={<Save size={14} />} label="Save document" primary />
            <ActBtn onClick={retake} icon={<Camera size={13} />} label="Retake" />
            <ActBtn onClick={onDone} icon={<X size={13} />} label="Cancel" />
          </div>
        </div>
      )}
    </div>
  );
}

// Captured-side thumbnails, so the driver can see what they've actually taken.
function Thumbs({ pages, sides }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      {pages.map((p, i) => (
        <div key={i} style={{ textAlign: 'center' }}>
          <div style={{ width: 78, height: 54, borderRadius: 6, border: '1px solid var(--hp-border)', overflow: 'hidden', background: 'var(--hp-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {p.contentType === 'application/pdf'
              ? <FileText size={20} color="var(--hp-dim)" />
              : <img alt="" src={`data:${p.contentType};base64,${p.base64}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>
          <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', marginTop: 3, fontWeight: 700 }}>
            {(sides && sides[i]) || `Page ${i + 1}`} <CheckCircle size={9} color="var(--hp-green)" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Structured-fields editor ───────────────────────────────────────────────────
function FieldsEditor({ type, item, api, reload, setBusy, setError, onDone }) {
  const [vals, setVals] = useState(() => {
    const v = {}; (type.fields || []).forEach(f => { v[f.key] = item?.fields?.[f.key] || ''; }); return v;
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setBusy(type.key + ':savef'); setError('');
    try {
      const d = await api({ action: 'saveFields', type: type.key, fields: vals });
      if (!d || !d.success) throw new Error(d?.error || 'Could not save');
      await reload(); onDone();
    } catch (e) { setError(e.message); } finally { setSaving(false); setBusy(''); }
  };
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--hp-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(type.fields || []).map(f => (
        <Field key={f.key} label={f.label}>
          <input value={vals[f.key]} placeholder={f.placeholder || ''}
            onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))} style={inputStyle} />
        </Field>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <ActBtn onClick={save} busy={saving} icon={<Save size={14} />} label="Save" primary />
        <ActBtn onClick={onDone} icon={<X size={13} />} label="Cancel" />
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 11px', fontSize: '0.86rem', border: '1px solid var(--hp-border)',
  borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)', boxSizing: 'border-box',
};
function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', letterSpacing: '0.03em', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}
// Consent to a VEVO check — vSure's agreement requires the visa holder's own consent
// before the office runs one. The wording is shown in full; giving or withdrawing it is
// recorded (with the date and wording version) on the visa record.
const VEVO_CONSENT_TEXT = 'I consent to Hollypaul (PH Bouveng) checking my visa and work rights with the Department of Home Affairs (VEVO), using my passport details, through its provider vSure — now and periodically while I work with Hollypaul.';
function VevoConsentPanel({ consent, api, reload, setError }) {
  const [busy, setBusy] = useState(false);
  const given = !!(consent && consent.given);
  const set = async (next) => {
    setBusy(true); setError('');
    try {
      const d = await api({ action: 'setVevoConsent', given: next });
      if (d && d.success) await reload(); else setError(d?.error || 'Could not save your consent');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const when = given && consent.at ? new Date(consent.at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return (
    <div style={{ marginBottom: 18, padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)',
      borderLeft: `3px solid ${given ? 'var(--hp-green)' : '#b8860b'}`, background: given ? 'rgba(46,125,50,0.06)' : 'rgba(201,168,76,0.10)' }}>
      <div style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--hp-text)', marginBottom: 4 }}>
        {given ? `Visa check consent given ${when}` : 'Visa check consent needed'}
      </div>
      <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginBottom: 8 }}>{VEVO_CONSENT_TEXT}</div>
      {given
        ? <ActBtn onClick={() => set(false)} busy={busy} label="Withdraw consent" />
        : <ActBtn onClick={() => set(true)} busy={busy} label="I consent" primary />}
    </div>
  );
}

function ActBtn({ onClick, busy, icon, label, primary }) {
  return (
    <button onClick={onClick} disabled={busy}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', fontSize: '0.8rem', fontWeight: 700, borderRadius: 'var(--radius)', cursor: 'pointer', opacity: busy ? 0.6 : 1,
        border: primary ? 'none' : '1px solid var(--hp-border)',
        background: primary ? 'var(--hp-gold)' : 'var(--hp-surface)',
        color: primary ? '#1a1a2e' : 'var(--hp-text)' }}>
      {icon} {busy ? '…' : label}
    </button>
  );
}
